import type { ScanReviewGroup } from "@hobbyshopify/backend-client";
import { describeCandidate, selectedProductId } from "../core/primitives/candidates";
import { cardSubtitle, fieldLabel, scanImage, scanTitle } from "../core/primitives/card-display";
import { hasCandidateChoice, needsCandidatePick } from "../core/primitives/review-row";

/**
 * One row of the review queue: every pending scan that read as the same card.
 *
 * Grouping is what makes the queue finishable. Two hundred dropped images are
 * perhaps a hundred and fifty distinct cards, and ten copies of one card are
 * one row here, one confirm, and one lot of ten — not ten of everything.
 *
 * The row confirms on its own: the price, the copies and, when the lookup was
 * unsure, which product it is, without opening the full editor.
 */
export function ReviewGroupRow({
  group,
  busy,
  price,
  quantity,
  showQuantity,
  canConfirm,
  pickingProductId,
  onPrice,
  onQuantity,
  onPick,
  onConfirm,
  onReview,
  onReject,
}: {
  group: ScanReviewGroup;
  busy?: boolean;
  price: string;
  quantity: number;
  /** False for a customer's scan: a price question, not stock coming in. */
  showQuantity: boolean;
  canConfirm: boolean;
  /** The candidate a pick is running for on this row. */
  pickingProductId: string | null;
  onPrice: (price: string) => void;
  onQuantity: (quantity: number) => void;
  onPick: (productId: string) => void;
  onConfirm: () => void;
  onReview: () => void;
  onReject: () => void;
}) {
  const exists = group.bucket === "exists";
  const unvetted = group.bucket === "unvetted";
  const title = group.card ? scanTitle({ ...group.sample, card: group.card }) : scanTitle(group.sample);
  const subtitle = cardSubtitle(group.sample);
  const picked = selectedProductId(group.sample.scpData);
  const mustPick = needsCandidatePick(group.sample);
  const candidates = hasCandidateChoice(group.sample) ? group.sample.candidates : [];
  const priceId = `review-price-${group.key}`;

  return (
    <div className="rounded-row border border-line bg-surface p-3 sm:p-4">
      <div className="flex gap-3">
        <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-thumb bg-surface-strong">
          {scanImage(group.sample) ? (
            <img src={scanImage(group.sample) ?? ""} alt={title} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-xs text-faint">No img</div>
          )}
          {group.count > 1 && (
            // The count sits on the thumbnail because it is a property of the
            // photograph pile, not of the card: ten scans, one card.
            <span className="absolute right-0 bottom-0 rounded-tl bg-surface-strong px-1 text-[10px] font-semibold text-white">
              ×{group.count}
            </span>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <p className="line-clamp-2 text-sm font-medium text-white">{title}</p>
            <span className={`badge shrink-0 ${exists ? "badge-neutral" : "badge-warn"}`}>
              {exists ? "Already exists" : unvetted ? "Needs review" : "New card"}
            </span>
          </div>
          {subtitle && <p className="mt-0.5 line-clamp-2 text-xs text-faint">{subtitle}</p>}
        </div>
      </div>

      <div className="mt-3">
        {unvetted && (
          <p className="mt-1 text-xs text-warn">A customer's scan created this card. Nobody has checked it yet.</p>
        )}
        {group.differences.length > 0 && (
          // The whole question the row asks. When the product key matched, the
          // two cards are the same product at the same grade and this is the
          // only place the reading disagrees at all.
          <ul className="mt-1 space-y-0.5">
            {group.differences.map((d) => (
              <li key={d.field} className="truncate text-xs text-faint">
                <span className="text-muted">{fieldLabel(d.field)}:</span>{" "}
                <span className="text-white">{d.scan || "—"}</span>
                {" vs "}
                <span className="text-white">{d.card || "—"}</span>
              </li>
            ))}
          </ul>
        )}

        {candidates.length > 0 && (
          <div className="mt-2">
            <p className={`text-xs ${mustPick ? "text-warn" : "text-muted"}`}>
              {mustPick ? "Which card is it? Pick one to price it." : "Not the right card?"}
            </p>
            <ul className="mt-1 flex flex-col gap-1">
              {candidates.map((c) => {
                const line = describeCandidate(c.product);
                const current = line.productId !== null && line.productId === picked;
                const picking = pickingProductId !== null && pickingProductId === line.productId;
                return (
                  <li key={`${c.rank}-${line.productId ?? line.title}`}>
                    <button
                      type="button"
                      disabled={busy || line.productId === null || current || pickingProductId !== null}
                      onClick={() => line.productId && onPick(line.productId)}
                      aria-pressed={current}
                      className={`flex w-full min-w-0 items-start gap-2 rounded-btn border px-3 py-2 text-left text-xs transition-colors ${
                        current
                          ? "border-accent text-white"
                          : "border-line text-muted hover:border-accent hover:text-white"
                      }`}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block text-white">{line.title}</span>
                        {line.subtitle && <span className="block text-faint">{line.subtitle}</span>}
                      </span>
                      <span className="shrink-0">
                        {picking ? "Switching…" : current ? "Picked" : c.matchedCardId ? "In stock" : "New"}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div className="mt-3 flex flex-wrap items-end gap-x-6 gap-y-3">
          <div>
            <label htmlFor={priceId} className="block text-[11px] text-muted">
              Price
            </label>
            <div className="flex items-center gap-1">
              <span className="text-sm text-muted">$</span>
              <input
                id={priceId}
                inputMode="decimal"
                className="field w-24 py-1.5"
                value={price}
                placeholder="0.00"
                onChange={(e) => onPrice(e.target.value)}
                disabled={busy}
              />
            </div>
          </div>
          {showQuantity && (
            <div>
              <span className="block text-[11px] text-muted">Copies</span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  className="btn-secondary btn-xs w-8"
                  onClick={() => onQuantity(quantity - 1)}
                  disabled={busy || quantity <= 0}
                  aria-label="One copy fewer"
                >
                  −
                </button>
                <span className="min-w-10 px-1 text-center text-sm text-white" aria-live="polite">
                  {quantity === 0 ? "Untracked" : quantity}
                </span>
                <button
                  type="button"
                  className="btn-secondary btn-xs w-8"
                  onClick={() => onQuantity(quantity + 1)}
                  disabled={busy}
                  aria-label="One copy more"
                >
                  +
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy || !canConfirm}
            className="btn-primary btn-xs whitespace-nowrap"
          >
            {busy ? "Confirming…" : group.count > 1 ? `Confirm all ${group.count}` : "Confirm"}
          </button>
          <button type="button" onClick={onReview} disabled={busy} className="btn-chip">
            Details
          </button>
          <button type="button" onClick={onReject} disabled={busy} className="btn-ghost">
            Reject
          </button>
        </div>
      </div>
    </div>
  );
}
